import { ApiResponseError } from "@shell/auth/apiClient";

interface FastEndpointsErrorBody {
    readonly errors?: Record<string, readonly string[]>;
    readonly message?: string;
}

/**
 * Best-effort extraction of a human-friendly message from an error
 * thrown by the API client. Handles FastEndpoints validation envelopes
 * (`errors: { field: [msg] }`) and falls back to the raw body or
 * native error text.
 */
export function describeApiError(error: unknown): string {
    if (error instanceof ApiResponseError) {
        if (error.body !== "") {
            try {
                const parsed = JSON.parse(error.body) as FastEndpointsErrorBody;
                if (parsed.errors !== undefined) {
                    const flattened = Object.values(parsed.errors).flat();
                    if (flattened.length > 0) {
                        return flattened.join(" ");
                    }
                }
                if (parsed.message !== undefined && parsed.message !== "") {
                    return parsed.message;
                }
            } catch {
                /* fall through */
            }
        }
        return `${String(error.status)} ${error.statusText}`;
    }
    if (error instanceof Error) {
        return error.message;
    }
    return "Unknown error";
}

/**
 * Returns true when the error came back as a 4xx with that status code.
 * Useful when the caller wants to branch on conflict vs other failures.
 */
export function isApiStatus(error: unknown, status: number): boolean {
    return error instanceof ApiResponseError && error.status === status;
}
